import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@up2d8/ui';
import { AdminIcon, type AdminIconName } from './icons';

/**
 * Uitlegvlak, lege toestand, melding, stappenbalk, lijstitem, tegel, code en de doelgroepkiezer
 * (spec A §5, vanaf "Uitlegvlak").
 */

export type NoteTone = 'neutral' | 'info' | 'attention' | 'danger';

const noteIcon: Record<NoteTone, AdminIconName> = {
  neutral: 'info',
  info: 'info',
  attention: 'alert',
  danger: 'alert',
};

/**
 * `.a-note`: radius 14, 13 px, icoon 18. Grijs (uitleg), `info` blauw, `attention` oranje,
 * `danger` rood. `lead` is het vetgedrukte begin dat de kern zegt.
 */
export function Note({
  tone = 'neutral',
  icon,
  lead,
  role,
  testId,
  children,
}: {
  tone?: NoteTone;
  /** Een eigen icoon van 18 in plaats van het standaardicoon van de toon. */
  icon?: ReactNode;
  lead?: ReactNode;
  role?: 'status' | 'alert';
  testId?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cn('a-note', tone !== 'neutral' && tone)} role={role} data-testid={testId}>
      {icon ?? <AdminIcon name={noteIcon[tone]} size={18} />}
      <div>
        {lead ? <strong>{lead}</strong> : null}
        {lead && children ? ' ' : null}
        {children}
      </div>
    </div>
  );
}

/** `.a-empty`: rond vlak van 56 met icoon, titel van 15 px, één zin, eventueel een knop. */
export function Empty({
  icon,
  title,
  text,
  action,
  testId,
}: {
  icon: ReactNode;
  title: ReactNode;
  text?: ReactNode;
  action?: ReactNode;
  testId?: string;
}) {
  return (
    <div className="a-empty" data-testid={testId}>
      <div className="a-empty-icon">{icon}</div>
      <div className="a-empty-title">{title}</div>
      {text ? <div className="a-empty-text">{text}</div> : null}
      {action ? <div style={{ marginTop: 8 }}>{action}</div> : null}
    </div>
  );
}

/**
 * De melding rechtsonder (`.a-toast`): donker, wit, groen vinkje, max. 420 breed. Vijf seconden.
 *
 * Een eigen melding en niet `useToast` uit `@up2d8/ui`: die staat rechtsboven en heeft de vorm van
 * de rest van het portaal. Zet `element` ergens in de pagina; de melding staat vast in het venster.
 */
export function useAdminToast(duration = 5000) {
  const [message, setMessage] = useState<ReactNode | null>(null);
  const timer = useRef<number | null>(null);

  const show = useCallback(
    (next: ReactNode) => {
      if (timer.current) window.clearTimeout(timer.current);
      setMessage(next);
      timer.current = window.setTimeout(() => setMessage(null), duration);
    },
    [duration],
  );

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const element = message ? <Toast>{message}</Toast> : null;
  return { show, element };
}

/** De melding zelf, voor wie ze zelf aan en uit zet. */
export function Toast({ children }: { children: ReactNode }) {
  return (
    <div className="a-toast" role="status" data-testid="admin-toast">
      <AdminIcon name="checkCircle" size={18} />
      <div>{children}</div>
    </div>
  );
}

/**
 * `.a-steps`: vakken met een nummer in een cirkel van 26 en een titel. Wat vóór `current` ligt, is
 * klaar (groen vinkje); `current` heeft de blauwe rand en `aria-current="step"`.
 */
export function Steps({
  steps,
  current,
  label,
}: {
  steps: ReactNode[];
  /** De index van de huidige stap, vanaf 0. */
  current: number;
  label?: string;
}) {
  const { t } = useTranslation();
  return (
    <ol className="a-steps" aria-label={label ?? t('beheer.kit.steps')}>
      {steps.map((step, index) => {
        const done = index < current;
        const on = index === current;
        return (
          <li
            key={index}
            className={cn('a-step', done && 'done', on && 'on')}
            aria-current={on ? 'step' : undefined}
          >
            <span className="a-step-num">
              {done ? <AdminIcon name="check" size={14} strokeWidth={3} /> : index + 1}
            </span>
            <span className="a-step-title">
              {step}
              {done ? <span className="a-sr"> ({t('beheer.kit.stepDone')})</span> : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export type TileTone = 'blue' | 'orange' | 'navy' | 'danger' | 'green';

/** `.a-tile`: tegel van 40, radius 12, tint per domein. */
export function Tile({
  tone = 'blue',
  size,
  children,
}: {
  tone?: TileTone;
  size?: number;
  children: ReactNode;
}) {
  return (
    <span
      className={cn('a-tile', tone !== 'blue' && tone)}
      style={size ? { width: size, height: size } : undefined}
      aria-hidden="true"
    >
      {children}
    </span>
  );
}

/**
 * `.a-item`: tegel links, titelregel met badges, metaregel, tekst; rechts een actie of chevron.
 */
export function ListItem({
  tile,
  tileTone,
  title,
  badges,
  meta,
  action,
  testId,
  children,
}: {
  tile?: ReactNode;
  tileTone?: TileTone;
  title: ReactNode;
  badges?: ReactNode;
  meta?: ReactNode;
  action?: ReactNode;
  testId?: string;
  children?: ReactNode;
}) {
  return (
    <div className="a-item" data-testid={testId}>
      {tile ? <Tile tone={tileTone}>{tile}</Tile> : null}
      <div className="a-item-info">
        <div className="a-item-top">
          <span className="a-name">{title}</span>
          {badges}
        </div>
        {meta ? <div className="a-meta">{meta}</div> : null}
        {children}
      </div>
      {action}
    </div>
  );
}

/**
 * Een code tonen (`.a-code`): 34 px, gestippelde rand. Altijd met de waarschuwing dat ze maar één
 * keer verschijnt; geef een eigen zin mee als die van de kit niet past.
 */
export function CodeDisplay({
  code,
  label,
  warning,
  testId,
}: {
  code: string;
  /** Wat de code is ("Koppelcode", "Aanmeldcode"); alleen voor een schermlezer. */
  label: string;
  warning?: ReactNode;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="a-code" data-testid={testId}>
        <span className="a-sr">{label}: </span>
        {code}
      </div>
      <Note tone="attention">{warning ?? t('beheer.kit.codeOnce')}</Note>
    </>
  );
}

export interface AudienceChip {
  key: string;
  label: string;
  onRemove?: () => void;
}

/**
 * De doelgroepkiezer, als weergave: label "Doelgroep", segment Iedereen / Kiezen, het blauwe vlak
 * met de samenvatting, en bij Kiezen de chips en de tekstknop "+ Groep, vestiging, rol of persoon".
 *
 * Wie er in de doelgroep zit en wat de knop opent, beslist het scherm: de kiezer zelf bestaat al in
 * het berichtenscherm en blijft daar.
 *
 * `error` staat onder het blauwe vlak, in de vorm van een veldfout (`Field`): bv. "Kiezen" zonder
 * keuze, die niet bewaard kan worden (hertest 30 okt.).
 */
export function AudiencePicker({
  mode,
  onModeChange,
  summary,
  chips = [],
  onAdd,
  addLabel,
  label,
  error,
  testId,
}: {
  mode: 'all' | 'pick';
  onModeChange: (mode: 'all' | 'pick') => void;
  /** De inhoud van het blauwe vlak: "<strong>51 medewerkers</strong> — Kiezen. Groepen: …". */
  summary: ReactNode;
  chips?: AudienceChip[];
  onAdd?: () => void;
  addLabel?: string;
  label?: string;
  error?: ReactNode;
  testId?: string;
}) {
  const { t } = useTranslation();
  const labelId = `${testId ?? 'audience'}-label`;
  return (
    <div className="a-field" data-testid={testId}>
      <div className="a-label" id={labelId}>
        {label ?? t('beheer.kit.audience.label')}
      </div>
      <div className="a-seg" role="radiogroup" aria-labelledby={labelId}>
        {(['all', 'pick'] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={mode === value}
            className={cn(mode === value && 'on')}
            onClick={() => onModeChange(value)}
          >
            {value === 'all' ? t('beheer.kit.audience.everyone') : t('beheer.kit.audience.pick')}
          </button>
        ))}
      </div>
      <Note tone="info" icon={<AdminIcon name="groups" size={18} />} role="status">
        {summary}
      </Note>
      {error ? (
        <div className="a-error" data-testid={testId ? `${testId}-error` : undefined}>
          <AdminIcon name="alert" size={16} />
          <span>{error}</span>
        </div>
      ) : null}
      {mode === 'pick' ? (
        <div className="a-actions">
          {chips.map((chip) => (
            <span key={chip.key} className="a-chip">
              {chip.label}
              {chip.onRemove ? (
                <button
                  type="button"
                  onClick={chip.onRemove}
                  aria-label={t('beheer.kit.removeChip', { label: chip.label })}
                >
                  <AdminIcon name="x" size={14} />
                </button>
              ) : null}
            </span>
          ))}
          {onAdd ? (
            <button type="button" className="a-btn ghost sm" onClick={onAdd}>
              <AdminIcon name="plus" size={16} />
              {addLabel ?? t('beheer.kit.audience.add')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
